import { PROFILE_SAMPLES } from './profiles'
import {
  hullOfCircles,
  profileFromPolygon,
  regularPolygonProfile,
  superellipseProfile,
  unionOfCirclesProfile
} from './shape'

/**
 * Shapes and colours offered by the bot customiser.
 *
 * Unlike animation silhouettes (`profiles.ts`), these are NOT measured from the
 * video: they are built analytically from the original customiser grid. Two
 * distinct sources, by design — animated states must stay faithful to the video,
 * base shapes are a user choice.
 */

/**
 * Identifiers are enumerated rather than inferred from the array: this lets the
 * i18n layer verify AT COMPILE TIME that every shape has its translation in all
 * three languages (`t(\`shapes.${id}\`)` only compiles if the key exists). An
 * `as const` on the array would have the same effect but would make `radii`
 * read-only, whereas the engine passes it through as-is.
 */
export type ShapeId =
  | 'etoile'
  | 'losange'
  | 'cercle'
  | 'galet'
  | 'squircle'
  | 'capsule'
  | 'triangle'
  | 'hexagone'
  | 'nuage'
  | 'goutte'
  | 'peta'
  | 'jovi'
  | 'curseur'
  | 'trio'

export interface BotShape {
  id: ShapeId
  radii: number[]
  /** No face — the silhouette is the whole character (pointer head). */
  faceless?: boolean
  /**
   * Multi-body group mark (Star + Peta + Jovi). Not a radial silhouette — the
   * customiser and stage render `LosiTrio` (three independent LosiBots) instead
   * of a single `LosiBot`. `radii` is only a catalogue placeholder so resolve
   * stays typed.
   */
  cluster?: boolean
  /**
   * Tilted Saturn ring around the body (Jovi / shirt look). Drawn by LosiBot
   * as a stroke ellipse; not part of the radial silhouette. Couples with
   * round-body volume in the engine.
   */
  ring?: boolean
  /**
   * Soft sphere / lobe foreshortening. Jovi = round rim; Peta = five soft tips.
   */
  volume?: 'round' | 'peta'
}

/** Clamps the maximum radius to `max` so all shapes look equally weighted. */
function normalize(radii: number[], max = 1): number[] {
  const peak = Math.max(...radii)
  if (peak <= 0) return radii
  const k = max / peak
  return radii.map((r) => r * k)
}

const ANGLES = Array.from({ length: PROFILE_SAMPLES }, (_, i) => (i / PROFILE_SAMPLES) * Math.PI * 2)

/** Pebble: circle deformed by two low harmonics, irregular but smooth. */
const pebble = normalize(
  ANGLES.map((a) => 1 + 0.075 * Math.cos(2 * a + 0.5) + 0.035 * Math.cos(3 * a + 2.1)),
  1.02
)

/** Cloud: union of lobes, wide at the bottom, two lobes at the top. */
const cloud = normalize(
  unionOfCirclesProfile([
    { x: -0.44, y: 0.2, r: 0.54 },
    { x: 0.46, y: 0.2, r: 0.5 },
    { x: 0.02, y: 0.3, r: 0.6 },
    { x: -0.24, y: -0.3, r: 0.48 },
    { x: 0.3, y: -0.24, r: 0.44 }
  ]),
  1.02
)

/** Droplet: large disc at the bottom, tapered point at the top. */
const droplet = normalize(
  profileFromPolygon(hullOfCircles(0, 0.28, 0.66, 0, -0.96, 0.05), 0, 0),
  1.04
)

/**
 * Peta — five soft lobes (rounded star): core disc plus five lobe discs, one
 * pointing up. Waists stay soft — a chubby silhouette, not a geometric star.
 */
function petaProfile(): number[] {
  const lobes = 5
  const dist = 0.4
  const lobeR = 0.5
  const circles: Array<{ x: number; y: number; r: number }> = [{ x: 0, y: 0, r: 0.52 }]
  for (let k = 0; k < lobes; k++) {
    const a = -Math.PI / 2 + (k * Math.PI * 2) / lobes
    circles.push({ x: Math.cos(a) * dist, y: Math.sin(a) * dist, r: lobeR })
  }
  return normalize(unionOfCirclesProfile(circles), 1.02)
}

/**
 * Jovi / “Lost” shirt character: main disc matches `cercle` (r = 1), plus
 * bottom-left and bottom-right discs on the same ground line (centre.y + r = 1).
 * Soft overlap keeps the three scallops shallow. Ring is `ring: true`.
 */
function joviProfile(): number[] {
  return unionOfCirclesProfile([
    { x: 0, y: 0, r: 1 },
    { x: -0.5, y: 0.5, r: 0.5 },
    { x: 0.5, y: 0.5, r: 0.5 }
  ])
}

/**
 * Mouse pointer head, no shaft. Fat body, big rounded tip (up-left) — a
 * curvy arrowhead, not a sharp triangle.
 */
function cursorHeadProfile(): number[] {
  return normalize(
    unionOfCirclesProfile([
      { x: -0.38, y: -0.5, r: 0.62 },
      { x: 0.18, y: -0.22, r: 0.52 },
      { x: -0.02, y: 0.18, r: 0.56 },
      { x: 0.22, y: 0.42, r: 0.34 }
    ]),
    1.12
  )
}

/** Horizontal capsule: envelope of two side-by-side discs. */
const capsule = profileFromPolygon(hullOfCircles(-0.42, 0, 0.62, 0.42, 0, 0.62), 0, 0)

/**
 * Star: four lobes with waists at ~61% — measured from the logo, with no floor
 * that would puff the centre into a circle (that was the « sparkle + round »).
 */
const SPARKLE_WAIST = 0.39
const etoile = normalize(
  ANGLES.map((a) => 1 - SPARKLE_WAIST * Math.sin(2 * a) ** 2),
  1.04
)

/** Four-lobe star profile (customiser default) — used for small dot states. */
export const SPARKLE_STAR_RADII = etoile

/** Diamond: rounded diamond, a more geometric variant. */
const losange = normalize(regularPolygonProfile(4, 1.04, 0.14, -90), 1.02)

export const SHAPES: BotShape[] = [
  { id: 'etoile', radii: etoile },
  { id: 'losange', radii: losange },
  { id: 'cercle', radii: new Array(PROFILE_SAMPLES).fill(1) },
  { id: 'galet', radii: pebble },
  // 1.15 not 1.02: on a superellipse the maximum radius is the diagonal,
  // so normalising on it yields a shape that looks smaller than the circle.
  { id: 'squircle', radii: normalize(superellipseProfile(4.2), 1.15) },
  { id: 'capsule', radii: capsule },
  // -90deg: one vertex toward the top of the screen (y points downward)
  { id: 'triangle', radii: regularPolygonProfile(3, 1.12, 0.34, -90) },
  // 0deg: vertices left and right, so top and bottom edges are flat
  { id: 'hexagone', radii: regularPolygonProfile(6, 1.04, 0.26, 0) },
  { id: 'nuage', radii: cloud },
  { id: 'goutte', radii: droplet },
  { id: 'peta', radii: petaProfile(), volume: 'peta' },
  { id: 'jovi', radii: joviProfile(), ring: true, volume: 'round' },
  { id: 'curseur', radii: cursorHeadProfile(), faceless: true },
  // Placeholder radii = star copy: trio never feeds the engine as one body.
  { id: 'trio', radii: etoile.slice(), cluster: true }
]

// Map indexed by `string` not `ShapeId`: callers query with a value read from
// localStorage or a prop, so unvalidated.
export const SHAPE_BY_ID = new Map<string, BotShape>(SHAPES.map((s) => [s.id, s]))
export const DEFAULT_SHAPE = 'etoile'

/** Old catalogue ids still found in localStorage / shared links. */
const SHAPE_ALIASES: Record<string, ShapeId> = {
  blob: 'peta',
  planete: 'jovi',
  terra: 'jovi'
}

/** Resolve a stored or linked shape id, including retired aliases. */
export function resolveShapeId(id: string | null | undefined): ShapeId | null {
  if (!id) return null
  const mapped = SHAPE_ALIASES[id] ?? id
  return SHAPE_BY_ID.has(mapped) ? (mapped as ShapeId) : null
}

const FACELESS_RADII = new Set(SHAPES.filter((s) => s.faceless).map((s) => s.radii))

/** Catalogue shapes that never wear a face (pointer head). */
export function isFacelessShape(radii: number[] | null | undefined): boolean {
  return !!radii && FACELESS_RADII.has(radii)
}

/** Multi-body group mark — stage/customiser use LosiTrio, not a single LosiBot. */
export function isClusterShape(id: string | null | undefined): boolean {
  if (!id) return false
  return !!SHAPE_BY_ID.get(id)?.cluster
}

export type ColorId =
  | 'blanc'
  | 'encre'
  | 'creme'
  | 'brun'
  | 'rouge'
  | 'orange'
  | 'ambre'
  | 'vert'
  | 'turquoise'
  | 'bleu'
  | 'violet'
  | 'rose'
  | 'gris'

export interface BotColor {
  id: ColorId
  hex: string
}

/** Original customiser palette. */
export const COLORS: BotColor[] = [
  { id: 'blanc', hex: '#ffffff' },
  { id: 'encre', hex: '#0a0a0c' },
  { id: 'brun', hex: '#8b5e3c' },
  { id: 'rouge', hex: '#e8483f' },
  { id: 'orange', hex: '#f08a24' },
  { id: 'ambre', hex: '#f0b429' },
  { id: 'vert', hex: '#3ecf8e' },
  { id: 'turquoise', hex: '#2fbfa0' },
  { id: 'bleu', hex: '#3b93f0' },
  { id: 'violet', hex: '#8b5cf6' },
  { id: 'rose', hex: '#e152b0' },
  { id: 'gris', hex: '#a3a3a3' },
  { id: 'creme', hex: '#f1efe9' }
]

export const COLOR_BY_ID = new Map<string, BotColor>(COLORS.map((c) => [c.id, c]))
export const DEFAULT_COLOR = 'creme'

/** Video black — also the default decor accent on light bodies. */
export const ENCRE = '#0a0a0c'

/** Interface highlight for decor on dark bodies. */
export const DECOR_LIGHT = '#d8dde3'

export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16)
  const chan = [n >> 16, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * chan[0]! + 0.7152 * chan[1]! + 0.0722 * chan[2]!
}

/**
 * Fill for decor dots that sit outside the body. Catalogue dots that merge with
 * the silhouette keep using the body colour; agent bubbles and magnifiers need
 * to read on both `blanc` stars and `encre` circles.
 */
export function decorAccent(bodyHex: string): string {
  return luminance(bodyHex) > 0.45 ? ENCRE : DECOR_LIGHT
}

/** Blends two hex colours. Used for particle depth fog. */
export function mixHex(from: string, to: string, t: number): string {
  const parse = (h: string) => {
    const v = parseInt(h.slice(1), 16)
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
  }
  const a = parse(from)
  const b = parse(to)
  const c = a.map((x, i) => Math.round(x + (b[i]! - x) * t))
  return `#${c.map((x) => x.toString(16).padStart(2, '0')).join('')}`
}
